import React, { useState, useEffect, useRef } from "react";
import {
  useDisclosure,
  Button,
  Flex,
  HStack,
  Text,
  IconButton,
  Box,
  Textarea,
  VStack,
  Avatar,
  useColorModeValue,
  Checkbox,
  CheckboxGroup,
  Stack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Portal,
  ButtonGroup,
  Tag,
  TagLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { AiOutlineComment, AiFillSetting } from "react-icons/ai";
import { socket } from "../../services/socket";
import Roles from "../../resources/Roles";
import { useAllUsers } from "../../hooks/Users";

const MESSAGE_SONG_URL =
  "https://public-resourses-files.s3.us-east-2.amazonaws.com/AUDIO/message.mp3";

const TAB_INDEX_CONSTANTS = {
  ROLES: {
    tabValue: 0,
    key: "ROLES",
  },
  USERS: {
    tabValue: 1,
    key: "USERS",
  },
};

const TAB_INDEX_VALUES = {
  0: "ROLES",
  1: "USERS",
};

const ProgramChat = ({ currentProgram = null }) => {
  const audio = new Audio(MESSAGE_SONG_URL);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [usersSelected, setUsersSelected] = useState([]);
  const { data: users = [], isLoading, isFetching, isError } = useAllUsers();
  const [tabIndex, setTabIndex] = useState(TAB_INDEX_CONSTANTS.ROLES.tabValue);
  const user = useSelector((state) => state.auth.user);
  const [rolesSelected, setRolesSelected] = useState(
    Roles.filter((r) => r.key === user?.role).map((r) => r.key)
  );
  const messagesEndRef = useRef(null);
  const programId = currentProgram?._id;
  const programType = currentProgram?.type;

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Join room and listen for messages
  useEffect(() => {
    if (!programId || !user) return;

    if (isOpen) {
      setHasNewMessages(false);
    }

    socket.emit("joinProgramRoom", {
      programId,
      programType,
      userId: user._id,
    });

    const handleProgramMessage = (msg) => {
      if (msg.programId === programId && msg.programType === programType) {
        // decide if the message is visible to this user
        const toUserIds = msg.toUserIds || [];
        const toRoles = msg.toRoles || [];
        const isForMe =
          msg.userId === user._id ||
          (toUserIds.length > 0 && toUserIds.includes(user._id)) ||
          (toRoles.length > 0 && toRoles.includes(user.role));

        if (isForMe) {
          setMessages((prev) => [...prev, msg]);
        }

        // notify if closed and message is for me and not from me
        if (
          !isOpen &&
          msg.userId !== user._id &&
          ((toUserIds.length > 0 && toUserIds.includes(user._id)) ||
            (toRoles.length > 0 && toRoles.includes(user.role)))
        ) {
          setHasNewMessages(true);
          audio.play();
        }
      }
    };

    socket.on("programMessage", handleProgramMessage);

    socket.emit("getProgramMessages", {
      programId,
      programType,
      role: user.role,
      userId: user._id,
    });

    socket.on("programMessagesHistory", (msgs) => {
      setMessages(msgs);
    });

    return () => {
      socket.emit("leaveProgramRoom", {
        programId,
        programType,
        userId: user._id,
      });
      socket.off("programMessage", handleProgramMessage);
      socket.off("programMessagesHistory");
    };
  }, [isOpen, programId, programType, user]);

  const handleSend = () => {
    if (!input.trim()) return;
    if (
      tabIndex === TAB_INDEX_CONSTANTS.ROLES.tabValue &&
      rolesSelected.length === 0
    )
      return;
    if (
      tabIndex === TAB_INDEX_CONSTANTS.USERS.tabValue &&
      usersSelected.length === 0
    )
      return;

    const msg = {
      programId,
      programType,
      userId: user._id,
      userName: `${user.name} ${user.lastName}`,
      role: user.role,
      message: input,
      createdAt: new Date().toISOString(),
      messageType: TAB_INDEX_VALUES[tabIndex],
      toUserIds: [],
      toRoles: [],
    };

    if (tabIndex === TAB_INDEX_CONSTANTS.ROLES.tabValue) {
      msg.toUserIds = [];
      msg.toRoles = rolesSelected;
    }
    if (tabIndex === TAB_INDEX_CONSTANTS.USERS.tabValue) {
      msg.toRoles = [];
      msg.toUserIds = usersSelected;
    }

    console.log("Sending message:", msg);

    socket.emit("programMessage", msg);
    setInput("");
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleTabsChange = (index) => {
    setTabIndex(index);
  };

  const bgMsg = useColorModeValue("gray.100", "gray.700");
  const myMsgBg = useColorModeValue("teal.100", "teal.700");

  return (
    <Flex justifyContent="flex-end">
      <Popover
        returnFocusOnClose={false}
        isOpen={isOpen}
        onClose={onClose}
        closeOnBlur={false}
        placement="right-start"
        isLazy
      >
        <PopoverTrigger>
          <Box position="fixed" bottom={6} right={6} zIndex={1000}>
            <Box position="relative">
              <IconButton
                isRound
                variant="solid"
                colorScheme="teal"
                size="lg"
                icon={<AiOutlineComment />}
                onClick={onToggle}
              />
              {hasNewMessages && !isOpen && (
                <Box
                  position="absolute"
                  top={0}
                  right={1}
                  width={4}
                  height={4}
                  bg="red.500"
                  borderRadius="full"
                  border="2px solid white"
                  zIndex={1}
                />
              )}
            </Box>
          </Box>
        </PopoverTrigger>
        <Portal>
          <PopoverContent w={{ base: "90vw", md: "500px" }} maxW="500px">
            <PopoverArrow />
            <PopoverHeader>Mensajes</PopoverHeader>
            <PopoverCloseButton />
            <PopoverBody bg={bgMsg}>
              <Flex direction="column" h="60vh">
                <Box flex={1} overflowY="auto" px={2} py={2}>
                  <VStack align="stretch" spacing={3}>
                    {messages.length === 0 && (
                      <Text color="gray.400" textAlign="center">
                        No hay mensajes
                      </Text>
                    )}
                    {messages.map((msg) => (
                      <Flex
                        key={msg._id}
                        align="flex-end"
                        justify={
                          msg.userId === user._id ? "flex-end" : "flex-start"
                        }
                      >
                        {msg.userId !== user._id && (
                          <Avatar size="sm" name={msg.userName} mr={2} />
                        )}
                        <Box
                          bg={msg.userId === user._id ? myMsgBg : "white"}
                          px={4}
                          py={2}
                          borderRadius="lg"
                          maxW="70%"
                          boxShadow="md"
                        >
                          <Text fontWeight="bold" fontSize="sm">
                            {msg.userName}{" "}
                            <Text as="span" color="gray.400" fontSize="xs">
                              (
                              {Roles.find((r) => r.key === msg.role)?.name ||
                                msg.role}
                              )
                            </Text>
                          </Text>
                          <Text>{msg.message}</Text>
                          <Text
                            color="gray.400"
                            fontSize="xs"
                            textAlign="right"
                          >
                            {new Date(msg.createdAt).toLocaleString()}
                          </Text>
                          <Flex align="center" mt={2} gap={2}>
                            {msg.messageType ===
                              TAB_INDEX_CONSTANTS.USERS.key && (
                              <>
                                <Tag
                                  size="sm"
                                  colorScheme="red"
                                  borderRadius="full"
                                >
                                  <TagLabel>Privado</TagLabel>
                                </Tag>
                                <Text color="gray.400" fontSize="xs">
                                  Privado para:{" "}
                                  {users
                                    .filter((u) =>
                                      msg.toUserIds.includes(u._id)
                                    )
                                    .map(
                                      (u) =>
                                        `${u.name || u.firstName || ""} ${
                                          u.lastName || ""
                                        }`.trim() || u.email
                                    )
                                    .join(", ")}
                                </Text>
                              </>
                            )}

                            {msg.messageType ===
                              TAB_INDEX_CONSTANTS.ROLES.key && (
                              <Text color="gray.400" fontSize="xs">
                                Disponible para:{" "}
                                {msg.toRoles &&
                                msg.toRoles.length === Roles.length
                                  ? "Todos los roles"
                                  : (msg.toRoles || [])
                                      .map(
                                        (key) =>
                                          Roles.find((r) => r.key === key)
                                            ?.name || key
                                      )
                                      .join(", ")}
                              </Text>
                            )}
                          </Flex>
                        </Box>
                        {msg.userId === user._id && (
                          <Avatar size="sm" name={msg.userName} ml={2} />
                        )}
                      </Flex>
                    ))}
                    <div ref={messagesEndRef} />
                  </VStack>
                </Box>
              </Flex>
            </PopoverBody>
            <PopoverFooter>
              <HStack>
                <Textarea
                  placeholder="Escribe un mensaje..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  bg="white"
                  resize="none"
                />
                <ButtonGroup isAttached>
                  <Button
                    colorScheme="teal"
                    onClick={handleSend}
                    isDisabled={!input.trim() || rolesSelected.length === 0}
                  >
                    Enviar
                  </Button>
                  {/* Roles selector */}
                  <Popover placement="right-start">
                    <PopoverTrigger>
                      <IconButton
                        icon={<AiFillSetting />}
                        aria-label="Select Roles"
                        colorScheme="orange"
                      />
                    </PopoverTrigger>
                    <PopoverContent>
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverHeader>Configuración de mensajes</PopoverHeader>
                      <PopoverBody>
                        <Tabs
                          isFitted
                          index={tabIndex}
                          onChange={handleTabsChange}
                        >
                          <TabList>
                            <Tab>Roles</Tab>
                            <Tab>Usuarios</Tab>
                          </TabList>
                          <TabPanels>
                            <TabPanel>
                              <CheckboxGroup
                                colorScheme="teal"
                                value={rolesSelected}
                                onChange={setRolesSelected}
                              >
                                <Stack
                                  direction="column"
                                  maxH="200px"
                                  overflowY="auto"
                                >
                                  {Roles.map((role) => (
                                    <Checkbox
                                      key={role.key}
                                      value={role.key}
                                      mr={2}
                                      mb={1}
                                    >
                                      {role.name}
                                    </Checkbox>
                                  ))}
                                </Stack>
                              </CheckboxGroup>
                            </TabPanel>
                            <TabPanel>
                              {isLoading || isFetching ? (
                                <Text>Cargando usuarios...</Text>
                              ) : isError ? (
                                <Text>Error al cargar usuarios</Text>
                              ) : users.length === 0 ? (
                                <Text>No hay usuarios en este programa</Text>
                              ) : (
                                <CheckboxGroup
                                  colorScheme="blue"
                                  value={usersSelected}
                                  onChange={(vals) => setUsersSelected(vals)}
                                >
                                  <Stack
                                    direction="column"
                                    maxH="200px"
                                    overflowY="auto"
                                  >
                                    {users
                                      .filter((u) => u._id !== user._id)
                                      .map((u) => (
                                        <Checkbox
                                          key={u._id}
                                          value={u._id}
                                          mr={2}
                                          mb={1}
                                        >
                                          {`${u.name || u.firstName || ""} ${
                                            u.lastName || ""
                                          }`.trim() ||
                                            u.email ||
                                            u._id}
                                        </Checkbox>
                                      ))}
                                  </Stack>
                                </CheckboxGroup>
                              )}
                            </TabPanel>
                          </TabPanels>
                        </Tabs>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                </ButtonGroup>
              </HStack>
              {tabIndex === TAB_INDEX_CONSTANTS.ROLES.tabValue && (
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Enviar mensaje a roles:{" "}
                  {rolesSelected.length === Roles.length
                    ? "Todos los roles"
                    : Roles.filter((r) => rolesSelected.includes(r.key))
                        .map((r) => r.name)
                        .join(", ")}
                </Text>
              )}

              {tabIndex === TAB_INDEX_CONSTANTS.USERS.tabValue && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Mensaje privado para:{" "}
                  {users
                    .filter((u) => usersSelected.includes(u._id))
                    .map(
                      (u) =>
                        `${u.name || u.firstName || ""} ${
                          u.lastName || ""
                        }`.trim() || u.email
                    )
                    .join(", ")}
                </Text>
              )}
            </PopoverFooter>
          </PopoverContent>
        </Portal>
      </Popover>
    </Flex>
  );
};

export default ProgramChat;
